"use client";

import { BackButton } from "@/components/BackButton";
import { useErrorHandler } from "@/components/hooks/use-error-handler";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  type LoginWithOtpError,
  loginWithOtp,
} from "@/server/actions/common/login-with-otp/login-with-otp";
import { pagesPath } from "@/utils/pages-path";
import { zodResolver } from "@hookform/resolvers/zod";
import InfoCircleIcon from "@public/icons/info-circle-icon.svg";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { MdHighlightOff } from "react-icons/md";
import { useTimer } from "react-timer-hook";
import { z } from "zod";
import { resendSignupOtp } from "./resend-otp";

const RESEND_COOLDOWN_SECONDS = 60;

const failureMessage = (error: LoginWithOtpError) => {
  switch (error) {
    case "invalidToken":
      return "入力に誤りがあります。正しく入力してください。";
    case "rateLimitExceeded":
      return "コードの試行回数が多すぎます。しばらく待ってからもう一度お試しください。";
    case "expiredCodeVerifier":
      return "コードの有効期限が切れています。もう一度、新規登録を行ってください。";
    case "codeVerifierNotFound":
      return "コードにエラーがあります。もう一度、新規登録を行ってください。";
    case "alreadyLoggedIn":
      return "ログイン中のため、処理を中断しました。";
    case "unknownError":
      return "コードを確認できませんでした。時間をおいてもう一度お試しいただくか、サポートにお問い合わせください。";
  }
};

const formSchema = z.object({
  token: z.string().min(1, {
    message: "コードが入力されていません。",
  }),
});

export const OtpForm = ({
  email,
  onCodeVerifierError,
  onBack,
}: {
  email: string;
  onCodeVerifierError: () => void;
  onBack: () => void;
}) => {
  const handleError = useErrorHandler();
  const router = useRouter();
  const { toast } = useToast();
  const [isSubmitting, startSubmitTransition] = useTransition();
  const [isResending, startResendTransition] = useTransition();

  // 初回OTP送信時からクールダウンを開始する
  const initialExpiry = new Date();
  initialExpiry.setSeconds(
    initialExpiry.getSeconds() + RESEND_COOLDOWN_SECONDS,
  );
  const { isRunning: isCooldownRunning, restart: restartCooldown } = useTimer({
    expiryTimestamp: initialExpiry,
    autoStart: true,
  });

  const canResend = !isCooldownRunning && !isResending;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      token: "",
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    startSubmitTransition(async () => {
      const result = await loginWithOtp(data);
      if (!result.success) {
        if (result.error === "invalidToken") {
          form.setError("token", {
            message: failureMessage(result.error),
          });
          return;
        }
        toast({
          title: "エラー",
          description: failureMessage(result.error),
          variant: "error",
        });
        if (
          result.error === "codeVerifierNotFound" ||
          result.error === "expiredCodeVerifier" ||
          result.error === "rateLimitExceeded"
        ) {
          onCodeVerifierError();
        } else if (result.error === "alreadyLoggedIn") {
          handleError("ALREADY_LOGGED_IN");
        }
        return;
      }
      toast({ title: "コードが正しく認識されました。" });
      form.reset();
      router.push(pagesPath.members.verify_account.$url().path);
    });
  });

  const handleResend = () => {
    startResendTransition(async () => {
      const result = await resendSignupOtp();
      if (result.success) {
        toast({ title: "コードを再送しました。", variant: "success" });
        const newExpiry = new Date();
        newExpiry.setSeconds(newExpiry.getSeconds() + RESEND_COOLDOWN_SECONDS);
        restartCooldown(newExpiry);
      } else {
        toast({
          title: "エラー",
          description:
            result.error === "rateLimitExceeded"
              ? "再送の試行回数が多すぎます。しばらく待ってからもう一度お試しください。"
              : "コードの再送に失敗しました。時間をおいてもう一度お試しください。",
          variant: "error",
        });
        if (
          result.error === "codeVerifierNotFound" ||
          result.error === "expiredCodeVerifier"
        ) {
          onCodeVerifierError();
        }
      }
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 説明テキスト */}
      <p className="text-sm font-semibold text-monotone-8">
        {email}
        宛にコードを送信しました。記載された6桁のコードを入力してください。
      </p>

      {/* フォーム */}
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
          {/* コード入力 */}
          <FormField
            name="token"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className="relative">
                <FormControl>
                  <Input
                    placeholder="6桁のコード"
                    inputMode="numeric"
                    type="text"
                    disabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                    {...field}
                  />
                </FormControl>
                <button
                  type="button"
                  className={cn(
                    "absolute top-0 right-1 flex h-10 items-center p-2",
                    !field.value && "hidden",
                  )}
                  onClick={() => form.setValue("token", "")}
                  aria-label="入力したコードをクリア"
                >
                  <MdHighlightOff className="fill-monotone-6 stroke-white" />
                </button>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 再送案内 */}
          <div className="flex flex-col items-start gap-2 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <span className="text-monotone-7">
              再送は1分経過後に送信してください。
            </span>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="text-primary-6 underline disabled:cursor-not-allowed disabled:text-monotone-5 disabled:no-underline"
            >
              コードを再送する
            </button>
          </div>

          {/* 認証メールが届かない場合 */}
          <div className="mt-4 flex flex-col gap-3">
            <span className="flex items-center gap-2 text-sm font-bold">
              <InfoCircleIcon
                className="h-4 w-4 shrink-0 text-primary-5"
                viewBox="0 0 20 20"
              />
              認証メールが届かない場合
            </span>
            <ul className="flex flex-col gap-1 pl-6 text-sm text-monotone-7">
              <li className="list-disc">
                ご入力いただいたメールアドレスが誤っている可能性があります。
              </li>
              <li className="list-disc">
                迷惑メールフォルダに振り分けられている可能性があります。受信トレイに届かない場合は迷惑メールフォルダをご確認ください。
              </li>
              <li className="list-disc">
                ドメイン指定受信をご利用されている方は、当サイトからメールを受信できるように設定してください。
              </li>
            </ul>
          </div>

          {/* ボタン + 前に戻る */}
          <div className="flex flex-col items-center gap-4">
            <Button
              className="w-full"
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting}
            >
              次へ
            </Button>
            <BackButton label="前に戻る" onClick={onBack} />
          </div>
        </form>
      </Form>
    </div>
  );
};
